import { Message } from "@/src/types/Entities/Messaging/Message";
import { Box, Divider, Rating, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import messageCenterMessageStyle from "./messageCenterMessageStyle";
import DownloadTile from "@/src/components/PetitionSummary/Documents/DownloadTile";
import { useState } from "react";
import { apiPatch, apiPost } from "@/src/services/apiClient";
import { queryKeys } from "@/src/services/reactQuery/reactQueryService";
import { useQueryClient } from "@tanstack/react-query";
import { CustomerFeedback } from "@/src/types/Entities/CustomerFeedback";
import { MinimalCase } from "@/src/types/Entities/MinimalCase";
import { useTheme } from "@mui/material/styles";
import ButtonLoading from "@/src/components/Button/ButtonLoading";
import dayjs from "dayjs";
import { mergeSx } from "@/src/utils/mergeSx";

function MessageCenterMessage({ message, product }: { message: Message; product: MinimalCase }) {
  const isCustomerMessage = message.senderType === "customer";
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [rating, setRating] = useState<number | null>(message.customerFeedback?.stars ?? null);
  const theme = useTheme();

  const submitFeedback = async () => {
    if (!rating) {
      return;
    }
    setIsLoading(true);
    if (message.customerFeedback) {
      await apiPatch(`/customer_feedbacks/${message.customerFeedback.id}`, {
        stars: rating,
      });
    } else {
      const feedback: CustomerFeedback = await apiPost("/customer_feedbacks", {
        stars: rating,
        source: "message",
        case: `/abstract_cases/${product.id}`,
      });
      await apiPatch(`/messages/${message.id}`, { customerFeedback: `/customer_feedbacks/${feedback.id}` });
    }
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("/conversations"),
    });
    setIsLoading(false);
  };

  return (
    <>
      <Box sx={messageCenterMessageStyle.messageContainer}>
        <Stack direction={"row"} sx={messageCenterMessageStyle.dateRow}>
          <Typography>{dayjs(message.created).format("DD.MM.YYYY, HH:mm") + " Uhr"}</Typography>
        </Stack>
        <Paper
          elevation={0}
          sx={mergeSx(
            messageCenterMessageStyle.messageBubble,
            isCustomerMessage ? messageCenterMessageStyle.ownMessageBubble(theme) : messageCenterMessageStyle.otherMessageBubble(theme),
          )}
        >
          <Typography sx={messageCenterMessageStyle.sender}>
            <strong>{message.senderType === "system" ? "Legalbird" : message.sender.messagingUserFullName}</strong>
          </Typography>
          <Typography
            component={"div"}
            dangerouslySetInnerHTML={{ __html: message.messageText }}
            sx={messageCenterMessageStyle.ownMessageText(theme)}
          />
          {message.attachments?.length > 0 && (
            <>
              <Divider sx={messageCenterMessageStyle.attachmentDivider} />
              <Grid container spacing={1} sx={messageCenterMessageStyle.attachmentGrid}>
                {message.attachments.map((mediaObject) => (
                  <Grid key={mediaObject.id} size={12}>
                    <DownloadTile mediaObject={mediaObject} />
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Paper>
        {!isCustomerMessage && message.showCustomerFeedback && (
          <Box sx={messageCenterMessageStyle.customerFeedbackBox}>
            <Typography color={"textSecondary"} variant={"subtitle2"} component={"h3"}>
              {message.customerFeedback ? "Vielen Dank für Ihre Bewertung." : "Wie hilfreich war diese Nachricht für Sie?"}
            </Typography>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={{ xs: 1, md: 2 }}
              sx={messageCenterMessageStyle.feedbackRow}
            >
              <Box sx={messageCenterMessageStyle.ratingContainer}>
                <Rating
                  name={`message-rating-${message.id}`}
                  value={rating ?? 0}
                  disabled={isLoading || !!message.customerFeedback}
                  onChange={async (event, stars) => setRating(stars)}
                  getLabelText={(value) => `${value} ${value === 1 ? "Stern" : "Sterne"}`}
                  slotProps={{ root: { role: "radiogroup", "aria-label": "Wie hilfreich war diese Nachricht für Sie?" } }}
                  sx={messageCenterMessageStyle.rating}
                />
              </Box>
              {rating && !message.customerFeedback && (
                <Box>
                  <ButtonLoading
                    sx={messageCenterMessageStyle.submitFeedbackButton}
                    size={"small"}
                    isLoading={isLoading}
                    onClick={submitFeedback}
                  >
                    Bewertung abschicken
                  </ButtonLoading>
                </Box>
              )}
            </Stack>
          </Box>
        )}
      </Box>
    </>
  );
}

export default MessageCenterMessage;
